import axios from 'axios';
import awsConfig from '../config/aws-config';

const api = axios.create({
  baseURL: awsConfig.apiUrl,
  headers: {
    'Content-Type': 'application/json'
  }
});

let tokenRefreshCallback = null;
let accessDeniedCallback = null;

// Setup token refresh interceptor
export const setupTokenRefresh = (refreshCallback) => {
  tokenRefreshCallback = refreshCallback;
};

export const setupAccessDeniedHandler = (callback) => {
  accessDeniedCallback = callback;
};

export function isAccessDeniedError(error) {
  return error.response?.status === 403 && error.response?.data?.code === 'ACCESS_DENIED';
}

// Add auth token to requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Axios interceptor to handle 401 errors and refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 401 and we haven't retried yet and we have a refresh callback
    if (error.response?.status === 401 && !originalRequest._retry && tokenRefreshCallback) {
      originalRequest._retry = true;

      try {
        // Call the refresh callback to get a new token
        const newToken = await tokenRefreshCallback();
        
        if (newToken) {
          // Update the token in the API service
          setAuthToken(newToken);
          
          // Update the failed request with the new token
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        return Promise.reject(refreshError);
      }
    }

    if (isAccessDeniedError(error) && accessDeniedCallback && !originalRequest._accessDeniedHandled) {
      originalRequest._accessDeniedHandled = true;
      accessDeniedCallback(error.response?.data?.message);
    }

    return Promise.reject(error);
  }
);

// File upload API call
export const uploadFile = async (file, fileType) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = async () => {
      try {
        const response = await api.post('/upload', {
          fileData: reader.result,
          fileName: file.name,
          mimeType: file.type,
          fileType // 'profile' or 'resume'
        });
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

// Resume API calls - Async generation with polling
export const generateResumeTemplate = async (data, onProgress) => {
  // Start async job
  const response = await api.post('/resume/generate', data);
  const { jobId } = response.data;
  
  // Poll for status
  return pollJobStatus(jobId, onProgress);
};

export const pollJobStatus = async (jobId, onProgress) => {
  const maxAttempts = 120; // 2 minutes max (120 * 1 second)
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    try {
      const response = await api.get(`/resume/status/${jobId}`);
      const { status, progress, message, resume } = response.data;
      
      // Call progress callback if provided
      if (onProgress) {
        onProgress({ status, progress, message });
      }
      
      // Check if completed
      if (status === 'completed') {
        return resume; // Return the final resume data
      }
      
      // Check if failed
      if (status === 'failed') {
        throw new Error(message || 'Resume generation failed');
      }
      
      // Wait 1 second before next poll
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;
      
    } catch (error) {
      if (error.response?.status === 404) {
        throw new Error('Job not found');
      }
      throw error;
    }
  }
  
  throw new Error('Resume generation timed out');
};

export const saveResume = async (resumeData) => {
  const response = await api.post('/resume/save', resumeData);
  return response.data;
};

export const getResumes = async ({ limit, nextToken } = {}) => {
  const params = {};
  if (limit != null) params.limit = limit;
  if (nextToken) params.nextToken = nextToken;
  const response = await api.get('/resume/list', { params });
  return response.data;
};

export const getResumeById = async (resumeId) => {
  const response = await api.get(`/resume/${resumeId}`);
  return response.data;
};

export const updateResume = async (resumeId, resumeData) => {
  const response = await api.put(`/resume/${resumeId}`, resumeData);
  return response.data;
};

export const deleteResume = async (resumeId) => {
  const response = await api.delete(`/resume/${resumeId}`);
  return response.data;
};

// Switch resume template without regenerating content (NEW)
export const switchResumeTemplate = async (resumeId, templateId) => {
  const response = await api.post(`/resume/${resumeId}/switch-template`, { templateId });
  return response.data;
};

// Download resume as PDF — throws on 402 (payment required)
export const downloadResumePDF = async (resumeId, filename = 'resume.pdf') => {
  const response = await api.get(`/resume/${resumeId}/download`, {
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  });

  // Build download link
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  if (contentDisposition) {
    const match = contentDisposition.match(/filename[^;=\n]*=\s*["']?([^"';\n]+)["']?/i);
    if (match?.[1]) filename = match[1].trim();
  }

  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  // Return remaining downloads count from response header (-1 = unlimited)
  const remaining = response.headers['x-downloads-remaining'];
  return remaining !== undefined ? parseInt(remaining, 10) : null;
};

// ─── Billing API ──────────────────────────────────────────────────────────────

export const getDownloadStatus = async (resumeId) => {
  const response = await api.get(`/resume/${resumeId}/download-status`);
  return response.data;
};

export const getSubscriptionStatus = async () => {
  const response = await api.get('/billing/subscription');
  return response.data;
};

export const getAccountSummary = async () => {
  const response = await api.get('/billing/account-summary');
  return response.data;
};

export const createCheckoutSession = async (type, resumeId) => {
  const response = await api.post('/billing/create-checkout-session', { type, resumeId });
  return response.data;
};

export const createPortalSession = async () => {
  const response = await api.get('/billing/portal');
  return response.data;
};

export const verifyCheckoutSession = async (sessionId) => {
  const response = await api.get(`/billing/verify-session?sessionId=${sessionId}`);
  return response.data;
};

// Template API calls (no auth required - but use same API instance for correct URL handling)
export const getTemplates = async () => {
  const response = await api.get('/templates');
  return response.data.templates;
};

export const getTemplateById = async (templateId) => {
  const response = await api.get(`/templates/${templateId}`);
  return response.data;
};

/** Fetch job posting text from a public careers-page URL */
export const fetchJobPosting = async (urlOrText) => {
  const response = await api.post('/job/fetch', { input: urlOrText });
  return response.data;
};

export default api;

